import { createContext, useContext, ReactNode } from 'react';

interface SidebarContextValue {
  /** Mobile/tablet drawer open state. Meaningless at xl and up, where the
   *  sidebar is a pinned column rather than a drawer. */
  isSidebarOpen: boolean;
  setIsSidebarOpen: (open: boolean) => void;
  /** Desktop (xl and up) collapse. Slides the pinned column off-canvas and
   *  drops the main content's left offset, so a wide page — the roster grid
   *  above all — gets the full viewport. Persisted per browser. */
  isSidebarCollapsed: boolean;
  setIsSidebarCollapsed: (collapsed: boolean) => void;
}

const SidebarContext = createContext<SidebarContextValue | null>(null);

interface SidebarProviderProps {
  children: ReactNode;
  value: SidebarContextValue;
}

export function SidebarProvider({ children, value }: SidebarProviderProps) {
  return (
    <SidebarContext.Provider value={value}>
      {children}
    </SidebarContext.Provider>
  );
}

export function useSidebar(): SidebarContextValue {
  const context = useContext(SidebarContext);
  if (!context) {
    // Fallback for use outside provider (backwards compatibility)
    return {
      isSidebarOpen: false,
      setIsSidebarOpen: () => {},
      isSidebarCollapsed: false,
      setIsSidebarCollapsed: () => {},
    };
  }
  return context;
}

/** localStorage key for the desktop collapse. */
export const SIDEBAR_COLLAPSED_KEY = 'ui.sidebarCollapsed';

/**
 * The left offset that clears the pinned sidebar. Carried once, by App.tsx's
 * content column (top bar + page), so no `<main>` repeats it.
 */
export function mainOffsetClass(collapsed: boolean): string {
  return collapsed ? 'xl:ml-0' : 'xl:ml-[268px]';
}

export default SidebarContext;
